<template>
	<base-modal
		:showCloseButton="true"
		:opened="isOpen"
		:maxWidth="'2xl'"
		@close="close"
	>
		<template #body>
			<div class="p-4 space-y-4">
				<div class="flex justify-center">
					<div
						class="bg-red-50 text-red-700 rounded-full px-4 py-2 inline-flex items-center gap-2 text-sm font-semibold"
					>
						<ExclamationTriangleIcon class="h-5 w-5" />
						<span>{{ urgencyText }}</span>
					</div>
				</div>

				<div class="text-center space-y-3">
					<h2 id="modal-headline" class="text-2xl md:text-3xl font-bold leading-tight">
						Your Access Is Ending. Your Growth Doesn’t Have To.
					</h2>
					<p class="text-xl text-gray-700 font-medium">
						Because you’ve been learning with Clover through your school, you
						unlock our best yearly price - <span class="font-bold">44% off!</span>
					</p>
					<p class="text-gray-700">
						Whether you’re strengthening concepts for class and clinicals, preparing for boards, or exploring new modalities - this one-time offer ensures you don’t lose the progress you’ve worked so hard to build.
					</p>
				</div>

				<div class="space-y-2">
					<div
						v-for="benefit in benefits"
						:key="benefit"
						class="flex items-start gap-2 bg-blue-50 rounded-md px-4 py-3"
					>
            <div class="w-6 h-6 shrink-0 mt-1">
						  <CheckCircleIcon class="h-full w-full text-green-600 -mt-1" />
            </div>
						<p class="text-gray-800 font-semibold">{{ benefit }}</p>
					</div>
				</div>

				<div class="space-y-4 !mt-6">
          <div class="flex justify-center">
            <div class="mx-auto flex flex-col gap-2">
              <div class="text-3xl font-bold">Yearly</div>
              <div class="text-2xl font-bold">
                <span class="line-through text-gray-900 text-5xl decoration-red-700 relative after:content-['$199'] after:absolute after:bottom-[95%] after:left-[95%] after:-rotate-12 after:text-red-700 after:font-semibold">$356</span>
                <span class="sr-only">$199</span>
                <span class="text-gray-600 text-lg font-medium ml-2">/every 12 months</span>
              </div>
            </div>
          </div>
					<p class="text-sm text-gray-500 text-center">
						Automatically billed every 12 months at $199. Cancel anytime.
					</p>
				</div>

				<div class="flex flex-col items-stretch gap-4">
					<button
						type="button"
						class="button rounded-md bg-yellow-400 text-black hover:bg-yellow-300 border-none px-6 py-3 text-base font-semibold w-full md:w-auto"
						@click="startCheckout"
					>
						Claim Deal &amp; Keep My Access
					</button>
					<button
						type="button"
						class="text-gray-600 hover:text-gray-800 underline text-sm"
						@click="handleDecline"
					>
            I don't want to keep my access <span aria-hidden="true">😢</span>
					</button>
				</div>
			</div>
		</template>
	</base-modal>
</template>

<script setup lang="ts">
import { computed, watch, PropType } from "vue";
import { analytics } from "@/services/segment.ts";
import { getEnv } from "@/env";
import BaseModal from "./BaseModal.vue";
import { useAccessRenewalModalStore } from "@/stores/accessRenewalModalStore";
import { useAccessRenewalOfferModalStore } from "@/stores/accessRenewalOfferModalStore";
import { useAcademicStudentAccessExpiration } from "@/hooks/useAcademicStudentAccessExpiration";
import { User } from "@/types/user";
import {
	ExclamationTriangleIcon,
	CheckCircleIcon,
} from "@heroicons/vue/24/solid";

const props = defineProps({
	user: {
		type: Object as PropType<User>,
		required: true,
	},
});

const benefits = [
	"Maintain full access to every video, quiz, and exam-style question - so your progress never stalls.",
	"Revisit challenging topics anytime and strengthen weak areas without waiting for lecture or clinical rotations.",
	"Continue developing professionally with 60+ hours of ASRT®-approved continuing education.",
];

const modalStore = useAccessRenewalModalStore();
const offerModalStore = useAccessRenewalOfferModalStore();
const isOpen = computed(() => modalStore.isOpen);

const { countdown } = useAcademicStudentAccessExpiration(props.user);

const urgencyText = computed(() => {
	if (countdown.value.isExpired) {
		return "Access expired";
	}

	if (countdown.value.days <= 0) {
		return `Only ${countdown.value.formattedDate.hours} ${countdown.value.formattedDate.hoursLabel} left to claim your partner-school discount`;
	}

	return `Only ${countdown.value.formattedDate.days} ${countdown.value.formattedDate.daysLabel} to claim your partner-school discount`;
});

const close = () => {
	analytics.track('B2B to D2C Offer Declined', { product: props.user.subscription_plan, days_remaining: countdown.value.days });
	modalStore.close(props.user.uuid);
	offerModalStore.open();
};

const startCheckout = () => {
	analytics.track('B2B to D2C Offer Accepted', { product: props.user.subscription_plan, days_remaining: countdown.value.days });
	modalStore.close(props.user.uuid);
	const params = new URLSearchParams(window.location.search);
	params.set("plan", getEnv('VITE_RENEWAL_OFFER_PLAN', 'professional'));
	params.set("frequency", getEnv('VITE_RENEWAL_OFFER_FREQUENCY', '1y'));
	window.location.href = `/account/checkout/offer-checkout-reg?${params.toString()}`;
};

const handleDecline = () => {
	close();
};

watch(isOpen, (val) => {
	if (val) analytics.track('B2B to D2C Offer Viewed', { product: props.user.subscription_plan, days_remaining: countdown.value.days });
});
</script>
